import { BALANCE as B, HOME, PREFERENCES, ROLES, TECHS } from "../core/data";
import type { FactionObservation, ObservedSystem } from "../core/fog";
import type { Fleet, GameAction, Technology } from "../core/types";
const value = (f: Fleet) =>
  f.ships.reduce(
    (v, u) =>
      v + B.ships[u.type].cost * Math.max(0.15, u.hull / B.ships[u.type].hull),
    0,
  );
function defense(s: ObservedSystem) {
  const us =
    s.ships ??
    (s.stars === 1
      ? ["scout"]
      : s.stars === 2
        ? ["scout", "cruiser"]
        : ["cruiser", "cruiser"]);
  return (
    us.reduce((v, t) => v + B.ships[t as keyof typeof B.ships].cost, 0) +
    (s.bastion ?? (s.stars === 4 ? 1 : 0)) * 165
  );
}
function firstStep(from: string, to: string, ss: ObservedSystem[]) {
  const by = Object.fromEntries(ss.map((s) => [s.id, s])),
    seen = new Set([from]),
    queue = [{ id: from, first: "" }];
  while (queue.length) {
    const x = queue.shift()!;
    for (const id of by[x.id]?.neighbors ?? []) {
      if (seen.has(id) || !by[id]) continue;
      const first = x.first || id;
      if (id === to) return first;
      seen.add(id);
      queue.push({ id, first });
    }
  }
  return null;
}
function observedDistance(from: string, to: string, systems: ObservedSystem[]) {
  if (from === to) return 0;
  const by = Object.fromEntries(systems.map((s) => [s.id, s])),
    seen = new Set([from]);
  let frontier = [from];
  for (let d = 1; d <= 36; d++) {
    const next: string[] = [];
    for (const id of frontier)
      for (const neighbor of by[id]?.neighbors ?? []) {
        if (neighbor === to) return d;
        if (!seen.has(neighbor) && by[neighbor]) {
          seen.add(neighbor);
          next.push(neighbor);
        }
      }
    if (!next.length) break;
    frontier = next;
  }
  return 1000;
}
/** Pure observation-only planner. Difficulty changes planning and risk, never hidden stats. */
export function decide(o: FactionObservation): {
  action: GameAction;
  reason: string;
} {
  const own = o.systems.filter((s) => s.owner === o.faction),
    by = Object.fromEntries(o.systems.map((s) => [s.id, s])),
    home = HOME[o.faction],
    distance = (from: string, to: string) =>
      observedDistance(from, to, o.systems),
    discount =
      1 - B.technology.infrastructureCostDiscount[o.tech.infrastructure],
    answer = (action: GameAction, reason: string) => ({ action, reason }),
    cost = (t: "mine" | "research" | "bastion", l: number) =>
      Math.round(
        (t === "mine"
          ? B.mining.upgradeCost[l]
          : t === "research"
            ? B.researchArray.upgradeCost[l]
            : B.bastion.buildOrUpgradeCost[l]) *
          discount *
          (t === "bastion" && o.faction === "xilur" ? 0.9 : 1),
      ),
    fleets = [...o.fleets].sort(
      (a, b) => value(b) - value(a) || a.id.localeCompare(b.id),
    );
  if (o.eliminated.includes(o.faction))
    return answer({ type: "end" }, "eliminated");
  if (!o.specialUsed && o.charge >= 80) {
    const fl = fleets.find(
      (f) =>
        by[f.system]?.owner === o.faction &&
        f.ships.some((u) => u.hull < B.ships[u.type].hull * 0.6),
    );
    if (fl)
      return answer(
        { type: "special", ability: "repairPulse", fleet: fl.id },
        "repair",
      );
  }
  const techOrder: Technology[] = [
    ...PREFERENCES[o.faction],
    ...TECHS.filter((t) => !PREFERENCES[o.faction].includes(t)),
  ];
  for (const technology of techOrder) {
    const level = o.tech[technology] + 1,
      c = Math.round(
        (B.technology.baseCostsByRank[level] ?? Infinity) *
          (PREFERENCES[o.faction].includes(technology) ? 0.85 : 1),
      );
    if (level <= 3 && o.rp >= c)
      return answer({ type: "research", technology }, "research");
  }
  const developed = [...own].sort(
      (a, b) =>
        (b.id === home ? 5 : 0) +
          b.stars -
          ((a.id === home ? 5 : 0) + a.stars) || a.id.localeCompare(b.id),
    ),
    hasResearch = own.some((s) => (s.research ?? 0) > 0);
  for (const s of developed) {
    if (s.builtTurn === o.ownTurn) continue;
    if (
      !hasResearch &&
      !s.mine &&
      !s.research &&
      o.credits >= cost("research", 1) + 70
    )
      return answer(
        { type: "build", system: s.id, building: "research" },
        "science-income",
      );
    if (
      !s.research &&
      (s.mine ?? 0) < 2 &&
      own.length > 1 &&
      o.credits >= cost("mine", (s.mine ?? 0) + 1) + 100
    )
      return answer(
        { type: "build", system: s.id, building: "mine" },
        "income",
      );
  }
  if (o.difficulty === "hard")
    for (const s of own) {
      const threat = o.systems
        .filter(
          (x) =>
            x.visible &&
            x.owner &&
            x.owner !== "unknown" &&
            x.owner !== o.faction &&
            distance(s.id, x.id) <= 2,
        )
        .reduce((v, x) => v + defense(x), 0);
      if (
        threat > 260 &&
        s.builtTurn !== o.ownTurn &&
        (s.bastion ?? 0) < 2 &&
        o.credits >= cost("bastion", (s.bastion ?? 0) + 1) + 100
      )
        return answer(
          { type: "build", system: s.id, building: "bastion" },
          "fortify",
        );
    }
  for (const fl of fleets) {
    const here = by[fl.system],
      desired = Math.min(
        7,
        o.difficulty === "easy" ? 4 : o.turn < 8 ? 4 : o.turn < 16 ? 5 : 7,
      );
    if (
      here?.owner === o.faction &&
      here.bastion &&
      fl.ships.length < desired
    ) {
      const t =
        o.credits >= Math.round(295 * discount) + 70 && fl.ships.length >= 3
          ? "dreadnought"
          : fl.ships.filter((s) => s.type === "cruiser").length < 3
            ? "cruiser"
            : "scout";
      if (o.credits >= Math.round(B.ships[t].cost * discount))
        return answer({ type: "ship", fleet: fl.id, ship: t }, "reinforce");
    }
  }
  if (
    own.length >= 3 &&
    o.fleets.length < 3 &&
    o.credits > 520 &&
    fleets.every((f) => f.ships.length >= 4)
  ) {
    const yard = own.find((s) => s.bastion),
      role = ROLES.find((r) => !o.fleets.some((f) => f.admiral.role === r));
    if (yard && role)
      return answer(
        { type: "commission", system: yard.id, role },
        "commission",
      );
  }
  const targets = o.systems.filter((s) => s.owner !== o.faction && !s.barrier);
  for (let index = 0; index < fleets.length; index++) {
    const fl = fleets[index];
    if (!fl.moves) continue;
    const power =
        value(fl) *
        (1 +
          o.tech.weapons * 0.07 +
          (o.faction === "reptiloids"
            ? B.factions.reptiloids.modifiers.attack - 1
            : 0)),
      ratio =
        o.difficulty === "easy" ? 1.45 : o.difficulty === "hard" ? 1.13 : 1.28,
      scored = targets
        .map((s) => ({
          s,
          score:
            s.stars * 32 +
            (s.home && s.owner && s.owner !== "unknown" ? 190 : 0) +
            (s.id.startsWith("core-") ? 45 : 0) -
            distance(fl.system, s.id) * 34 -
            defense(s) * 0.18 -
            index * (s.id === targets[0]?.id ? 25 : 0),
        }))
        .sort((a, b) => b.score - a.score || a.s.id.localeCompare(b.s.id));
    for (const { s } of scored) {
      const first = firstStep(fl.system, s.id, o.systems),
        step = first ? by[first] : null;
      if (!step || step.barrier) continue;
      if (step.owner !== o.faction && power < defense(step) * ratio) continue;
      if (
        !o.specialUsed &&
        step.visible &&
        step.owner !== o.faction &&
        by[fl.system]?.owner === o.faction
      ) {
        if (step.bastion && o.charge >= 85)
          return answer(
            { type: "special", ability: "sabotageProbe", system: step.id },
            "sabotage",
          );
        if ((step.ships?.length ?? 0) >= 3 && o.charge >= 110)
          return answer(
            { type: "special", ability: "ionTempest", system: step.id },
            "ion",
          );
      }
      if (
        fl.ships.some((u) => u.hull < B.ships[u.type].hull * 0.4) &&
        by[fl.system]?.owner === o.faction &&
        !by[fl.system].bastion
      ) {
        const yard = own
            .filter((s) => s.bastion)
            .sort(
              (a, b) => distance(fl.system, a.id) - distance(fl.system, b.id),
            )[0],
          back = yard && firstStep(fl.system, yard.id, o.systems);
        if (back && by[back].owner === o.faction)
          return answer(
            { type: "move", fleet: fl.id, to: back },
            "repair-route",
          );
      }
      return answer(
        { type: "move", fleet: fl.id, to: first! },
        step.owner === o.faction ? "approach" : "expand",
      );
    }
  }
  if (!o.specialUsed) {
    const danger = o.systems.some(
      (s) =>
        s.visible &&
        s.owner &&
        s.owner !== "unknown" &&
        s.owner !== o.faction &&
        distance(home, s.id) === 1 &&
        defense(s) > 200,
    );
    if (
      danger &&
      by[home]?.owner === o.faction &&
      o.charge >= 95 &&
      !by[home].barrier
    )
      return answer(
        { type: "special", ability: "phaseBarrier", system: home },
        "protect-home",
      );
    if (o.charge >= 100)
      for (const f of fleets.filter(
        (f) =>
          f.moves > 0 &&
          by[f.system]?.owner === o.faction &&
          f.slipUsedTurn !== o.ownTurn,
      )) {
        const front = own
          .filter(
            (s) =>
              s.id !== f.system &&
              distance(f.system, s.id) <= 3 &&
              s.neighbors.some((id) => by[id]?.owner !== o.faction),
          )
          .sort((a, b) => distance(f.system, b.id) - distance(f.system, a.id));
        if (front[0] && distance(f.system, front[0].id) >= 2)
          return answer(
            {
              type: "special",
              ability: "slipstreamCorridor",
              fleet: f.id,
              to: front[0].id,
            },
            "redeploy",
          );
      }
    if (o.charge >= 70 && o.systems.length < 24 && o.turn > 6)
      return answer({ type: "special", ability: "sensorBurst" }, "scan");
  }
  for (const s of developed) {
    if (s.builtTurn === o.ownTurn) continue;
    if (
      !s.mine &&
      (s.research ?? 0) < 3 &&
      o.credits >= cost("research", (s.research ?? 0) + 1) + 90
    )
      return answer(
        { type: "build", system: s.id, building: "research" },
        "science-upgrade",
      );
    if (
      !s.bastion &&
      o.credits >= cost("bastion", 1) + 145 &&
      o.fleets.some((f) => f.system === s.id)
    )
      return answer(
        { type: "build", system: s.id, building: "bastion" },
        "forward-yard",
      );
  }
  return answer({ type: "end" }, "complete");
}
